import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, fmtDate, inr } from "@/lib/api";
import { DataTable, DateRangeFilter, Money, PageHeader } from "@/components/common";
import { cn } from "@/lib/utils";

export default function Ledgers() {
  const [ledgers, setLedgers] = useState([]);
  const [selected, setSelected] = useState("");
  const [detail, setDetail] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/ledgers").then((r) => setLedgers(r.data.ledgers)).catch(() => {});
  }, []);

  const loadDetail = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const r = await api.get("/ledgers/detail", { params: { name: selected, from_date: range.from, to_date: range.to } });
      setDetail(r.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [selected, range]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const groups = [...new Set(ledgers.map((l) => l.group))];

  return (
    <div data-testid="ledgers-page">
      <PageHeader title="Accounting Ledgers" subtitle="Double-entry style books — customers, GST, cash, bank, sales, expenses"
        testid="ledgers-header" />
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-3 max-h-[70vh] overflow-y-auto" data-testid="ledger-list">
          {groups.map((g) => (
            <div key={g} className="mb-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#5C6B69] px-2 py-1">
                {g.replace("_", " ")}</div>
              {ledgers.filter((l) => l.group === g).map((l) => (
                <button key={l.ledger} onClick={() => setSelected(l.ledger)}
                  data-testid={`ledger-${l.ledger.replace(/[^a-z0-9]/gi, "-")}`}
                  className={cn("w-full text-left px-2.5 py-2 rounded-lg text-sm transition-colors",
                    selected === l.ledger ? "bg-[#0D4F4A] text-white" : "hover:bg-[#FAF8F5]")}>
                  <div className="flex justify-between items-center gap-2">
                    <span className="truncate font-medium">{l.label}</span>
                    <span className={cn("num text-xs shrink-0", selected === l.ledger ? "text-[#D4AF37]" : l.balance < 0 ? "text-red-600" : "text-[#0D4F4A]")}>
                      {inr(Math.abs(l.balance))}{l.balance < 0 ? " Cr" : " Dr"}</span>
                  </div>
                </button>
              ))}
            </div>
          ))}
          {ledgers.length === 0 && <p className="text-sm text-[#5C6B69] p-3">No ledger entries yet. Create invoices, payments or expenses.</p>}
        </div>
        <div className="lg:col-span-3">
          {!selected ? (
            <div className="bg-white border border-[#E5E0D8] rounded-xl shadow-sm p-12 text-center text-[#5C6B69]">
              Select a ledger to view its statement</div>
          ) : loading || !detail ? (
            <div className="py-24 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-[#5C6B69]" /></div>
          ) : (
            <div data-testid="ledger-detail">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="text-sm">
                  <span className="text-[#5C6B69]">Opening:</span> <b className="num">{inr(detail.opening)}</b>
                  <span className="text-[#5C6B69] ml-4">Closing:</span>{" "}
                  <b className="num text-[#0D4F4A]" data-testid="ledger-closing">{inr(detail.closing)}</b>
                </div>
                <DateRangeFilter from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} testid="ledger-date-filter" />
              </div>
              <DataTable testid="ledger-entries"
                columns={[
                  { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
                  { key: "ref_no", label: "Ref", render: (r) => r.ref_no || "—" },
                  { key: "narration", label: "Narration", render: (r) => <span className="text-xs">{r.narration}</span> },
                  { key: "debit", label: "Debit", align: "right", render: (r) => r.debit ? <Money value={r.debit} /> : "—" },
                  { key: "credit", label: "Credit", align: "right", render: (r) => r.credit ? <Money value={r.credit} /> : "—" },
                  { key: "balance", label: "Balance", align: "right", render: (r) => <Money value={r.balance} className="font-semibold" /> },
                ]}
                rows={detail.entries} empty="No entries in this period" />
              <div className="text-xs text-[#5C6B69] mt-2 text-right">
                Total Dr {inr(detail.total_debit)} · Total Cr {inr(detail.total_credit)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
